import React, { useState, useEffect, useCallback } from "react";
import {
  Button,
  Card,
  CardBody,
  CardTitle,
  Col,
  Container,
  Form,
  Input,
  Label,
  Row,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Alert
} from "reactstrap";
import Breadcrumbs from "../../../components/Common/Breadcrumb";
import classnames from "classnames";

// 3rd Party Libraries
import { useFormik } from "formik";
import toastr from 'toastr';
import axios from 'axios';
import "toastr/build/toastr.min.css";

// CKEditor Imports
import Editor from 'ckeditor5-custom-build/build/ckeditor';
import { CKEditor } from '@ckeditor/ckeditor5-react';

const SubseaContentManager = () => {
  document.title = "Site İçerikleri | Subsea Admin";

  const API_URL = "https://subseaapi.stechomeyazilim.info/SiteContents";
  
  const [activeTab, setActiveTab] = useState("about-us");
  const [loading, setLoading] = useState(false);
  const [contents, setContents] = useState([]);

  const safeJsonParse = (str) => {
    try {
      if (typeof str === 'object' && str !== null) return str;
      return JSON.parse(str);
    } catch (e) {
      return str; 
    }
  };

  // --- DÜZELTME 1: Veri çekme fonksiyonunu bağımsız hale getirdik ---
  const fetchContents = useCallback(async () => {
    try {
      // Sadece ilk yüklemede loading gösterelim, güncelleme sonrası tüm ekran titremesin
      // Ancak kullanıcı deneyimi için loading state'ini koruyabilirsiniz.
      // setLoading(true); 
      const response = await axios.get(API_URL);
      const data = Array.isArray(response.data.value) ? response.data.value : response.data.SiteContents || response.data;
      setContents(data);
    } catch (error) {
      console.error("Veri çekme hatası:", error);
      toastr.error("İçerikler yüklenemedi.");
    } finally {
      // setLoading(false);
    }
  }, []);

  // Sayfa ilk açıldığında veriyi çek
  useEffect(() => {
    setLoading(true);
    fetchContents().then(() => setLoading(false));
  }, [fetchContents]);

  const toggleTab = (tab) => {
    if (activeTab !== tab) setActiveTab(tab);
  };

  // --- Form Bileşeni: About Us ---
  // DÜZELTME 2: onRefresh prop'u eklendi
  const AboutUsForm = ({ data, onRefresh }) => {
    const parsedContent = safeJsonParse(data.ContentJson) || { mission: "", paragraphs: [] };
    
    const initialParagraphs = Array.isArray(parsedContent.paragraphs) 
      ? parsedContent.paragraphs.join("\n\n") 
      : parsedContent.paragraphs || "";

    const formik = useFormik({
      enableReinitialize: true, // Data değişince formu yeniler
      initialValues: {
        mission: parsedContent.mission || "",
        paragraphs: initialParagraphs
      },
      onSubmit: async (values) => {
        try {
          setLoading(true);
          const contentObject = {
            mission: values.mission,
            paragraphs: values.paragraphs.split("\n\n").filter(p => p.trim() !== "")
          };

          const updateModel = {
            ID: data.ID,
            PageKey: data.PageKey,
            Title: data.Title,
            ContentJson: JSON.stringify(contentObject),
            UpdatedAt: new Date().toISOString()
          };

          await axios.put(`${API_URL}(${data.ID})`, updateModel);
          toastr.success("Hakkımızda içeriği güncellendi.");
          
          // DÜZELTME 3: Başarılı güncelleme sonrası veriyi yenile
          if (onRefresh) await onRefresh();

        } catch (error) {
          console.error("Güncelleme hatası:", error);
          toastr.error("Hata oluştu.");
        } finally {
          setLoading(false);
        }
      }
    });

    return (
      <Form onSubmit={formik.handleSubmit}>
        <Row>
          <Col md={12} className="mb-3">
            <Label>Misyon (Mission)</Label>
            <Input
              type="textarea"
              rows="3"
              name="mission"
              value={formik.values.mission}
              onChange={formik.handleChange}
              className="form-control"
            />
          </Col>
          <Col md={12} className="mb-3">
            <Label>Paragraflar (Her paragraf arasına boş bir satır bırakın)</Label>
            <Input
              type="textarea"
              rows="12"
              name="paragraphs"
              value={formik.values.paragraphs}
              onChange={formik.handleChange}
              className="form-control"
            />
          </Col>
        </Row>
        <Button color="primary" type="submit" disabled={loading}>
            {loading ? "Kaydediliyor..." : "Hakkımızda Güncelle"}
        </Button>
      </Form>
    );
  };

  // --- Form Bileşeni: HTML İçerikler ---
  // DÜZELTME 2: onRefresh prop'u eklendi
  const HtmlContentForm = ({ data, onRefresh }) => {
    const [editorData, setEditorData] = useState(data.ContentJson || "");

    // DÜZELTME 4: Eğer üstten gelen data değişirse (onRefresh sonrası), editörü güncelle
    useEffect(() => {
        setEditorData(data.ContentJson || "");
    }, [data]);

    const handleSave = async () => {
      try {
        setLoading(true);
        const updateModel = {
          ID: data.ID,
          PageKey: data.PageKey,
          Title: data.Title,
          ContentJson: editorData, 
          UpdatedAt: new Date().toISOString()
        };

        await axios.put(`${API_URL}(${data.ID})`, updateModel);
        toastr.success(`${data.Title || data.PageKey} güncellendi.`);

        // DÜZELTME 3: Başarılı güncelleme sonrası veriyi yenile
        if (onRefresh) await onRefresh();

      } catch (error) {
        console.error("Güncelleme hatası:", error);
        toastr.error("Hata oluştu.");
      } finally {
        setLoading(false);
      }
    };

    return (
      <div>
        <div className="mb-3 border rounded">
          <CKEditor
            editor={Editor}
            data={editorData}
            onChange={(event, editor) => {
              setEditorData(editor.getData());
            }}
          />
        </div>
        <Button color="success" onClick={handleSave} disabled={loading}>
          {loading ? "Kaydediliyor..." : "İçeriği Kaydet"}
        </Button>
      </div>
    );
  };

  const aboutData = contents.find(c => c.PageKey === "about-us" || c.ID === 8);
  const privacyData = contents.find(c => c.PageKey === "privacy-policy" || c.ID === 5);
  const termsData = contents.find(c => c.PageKey === "terms-of-service" || c.ID === 4);
  const howItWorksData = contents.find(c => c.PageKey === "how-it-works" || c.PageKey === "how_it_works");

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Yönetim" breadcrumbItem="Site İçerikleri" />

          <Row>
            <Col xs="12">
              <Card>
                <CardBody>
                  <CardTitle className="h4 mb-4">İçerik Yönetimi</CardTitle>

                  <Nav tabs>
                    <NavItem>
                      <NavLink
                        className={classnames({ active: activeTab === "about-us" })}
                        onClick={() => toggleTab("about-us")}
                        style={{ cursor: "pointer" }}
                      >
                        Hakkımızda
                      </NavLink>
                    </NavItem>
                    <NavItem>
                      <NavLink
                        className={classnames({ active: activeTab === "how-it-works" })}
                        onClick={() => toggleTab("how-it-works")}
                        style={{ cursor: "pointer" }}
                      >
                        Nasıl Çalışır?
                      </NavLink>
                    </NavItem>
                    <NavItem>
                      <NavLink
                        className={classnames({ active: activeTab === "privacy-policy" })}
                        onClick={() => toggleTab("privacy-policy")}
                        style={{ cursor: "pointer" }}
                      >
                        Gizlilik Politikası
                      </NavLink>
                    </NavItem>
                    <NavItem>
                      <NavLink
                        className={classnames({ active: activeTab === "terms-of-service" })}
                        onClick={() => toggleTab("terms-of-service")}
                        style={{ cursor: "pointer" }}
                      >
                        Kullanım Şartları
                      </NavLink>
                    </NavItem>
                  </Nav>

                  <TabContent activeTab={activeTab} className="p-3 text-muted">
                    
                    <TabPane tabId="about-us">
                      <Row>
                        <Col sm="12">
                          {aboutData ? (
                            // DÜZELTME 5: onRefresh prop'u buraya geçirildi
                            <AboutUsForm data={aboutData} onRefresh={fetchContents} />
                          ) : (
                            <Alert color="warning">Hakkımızda verisi bulunamadı.</Alert>
                          )}
                        </Col>
                      </Row>
                    </TabPane>

                    <TabPane tabId="how-it-works">
                      <Row>
                        <Col sm="12">
                          {howItWorksData ? (
                            // DÜZELTME 5: onRefresh prop'u buraya geçirildi
                            <HtmlContentForm data={howItWorksData} onRefresh={fetchContents} />
                          ) : (
                            <Alert color="warning">
                                'Nasıl Çalışır' verisi bulunamadı.
                            </Alert>
                          )}
                        </Col>
                      </Row>
                    </TabPane>

                    <TabPane tabId="privacy-policy">
                      <Row>
                        <Col sm="12">
                          {privacyData ? (
                            // DÜZELTME 5: onRefresh prop'u buraya geçirildi
                            <HtmlContentForm data={privacyData} onRefresh={fetchContents} />
                          ) : (
                             <Alert color="warning">Gizlilik verisi bulunamadı.</Alert>
                          )}
                        </Col>
                      </Row>
                    </TabPane>

                    <TabPane tabId="terms-of-service">
                      <Row>
                        <Col sm="12">
                           {termsData ? (
                            // DÜZELTME 5: onRefresh prop'u buraya geçirildi
                            <HtmlContentForm data={termsData} onRefresh={fetchContents} />
                          ) : (
                             <Alert color="warning">Kullanım Şartları verisi bulunamadı.</Alert>
                          )}
                        </Col>
                      </Row>
                    </TabPane>

                  </TabContent>

                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default SubseaContentManager;